// The mock preview (BUN_PUBLIC_USE_MOCKS=true; covers from /mock/img/, served by src/index.ts in mock mode; token
// "mock" signs in). The board, chat, the graph and record details are the real client core's (@hq-org/platform-wasm, the
// same `createKitApi` a signed-in session uses) over a mock instance serving the POC's sample data (instance.ts,
// records.ts): no board or chat rule is written here. The rest (sessions, connections, members, tokens, ontologies,
// secrets, usage, search) is sample data in memory.
import { createPlatformKit } from "@hq-org/platform-wasm";
import type { Kit } from "@hq-org/kit-wasm";
import { sleep } from "@/lib/utils";
import { createKitApi } from "../kit";
import type { Net } from "../net";
import { type Api, ApiError, type AppGrant, type Managed, type ReviewItem, type SignIn } from "../types";
import { MOCK_TOKEN } from "./fixtures";
import { mockInstance } from "./instance";
import { diffOf, INSTANCES, platformFixture } from "./platform";
import { POC_CONTEXT, POC_LINKS, pocBody, pocFixture } from "./poc";
import { mockLogs } from "./records";
import { mockSecrets } from "./secrets";

const state = pocFixture();
const p = platformFixture(state);
const ME = state.persons.find((x) => x.name === "Michael")?.id ?? state.persons[0]!.id;

let kinds: Promise<any[]> | null = null;
const instance = mockInstance({
	logs: mockLogs(state, p, { me: ME, body: pocBody, context: POC_CONTEXT, links: POC_LINKS }),
	me: ME,
	spaces: state.projects.filter((x) => !x.stub && !x.instance).map((x) => x.id),
	// the real engine's answer for the base package, hq.tasks and hq.chat (tools/mock-kinds.ts)
	kinds: () => (kinds ??= fetch("/mock/kinds.json").then((r) => r.json())),
});

let kit: Promise<Kit> | null = null;
/** The preview's kit: the platform's client core with the sample instance as its home (no socket, nothing stored). */
const meter = (metric: string, used: number, limit: number, period: "day" | "month") => ({ service: "workers", metric, used, limit, unit: "count", period, scope: null, resetsAt: null, pct: Math.round((used / limit) * 1000) / 10, source: "", error: null });
const fleetBase = { instanceId: null, status: "live", region: "eeur", error: null, hosting: "hst_mock", account: "8a9957116eaa5e2b0101cdc99038b091", auto: false, held: null };
const chatSource = (id: string, line: number, date: string, quote: string) => ({ id, type: "MESSAGE", title: `main-business ${date}`, path: "main-business/tg-chat-20261026.md", lines: { from: line, to: line }, page: null, date, quote, file: null });
/** the mock housekeeper batch (knowledge.decide edits it in place) */
const review: ReviewItem[] = ([
	{ id: "prp_mock4", key: "subscription/virality-vercel", status: "waiting", kind: "Subscription", op: "create", summary: "Subscription Vercel Pro", why: null, error: "subscription: is active but has no renewsAt", fields: [["name", "Vercel Pro"], ["status", "active"]], sources: [chatSource("src_mock4", 512, "2026-09-02T11:20", "\u043f\u0440\u043e\u0434\u043b\u0438\u043b\u0438 Vercel Pro")], questions: [{ field: "renewsAt", waitsFor: null, text: "Subscription Vercel Pro: when does it renew next (a date)? The sources do not say." }], answers: {} },
	{ id: "prp_mock1", key: "person/barannik-s", status: "draft", kind: "Person", op: "create", summary: "Person \u0421\u043e\u0444\u044c\u044f \u0411\u0430\u0440\u0430\u043d\u043d\u0438\u043a", why: null, error: null, fields: [["name", "\u0421\u043e\u0444\u044c\u044f \u0411\u0430\u0440\u0430\u043d\u043d\u0438\u043a"], ["type", "human"]], sources: [chatSource("src_mock1", 8, "2026-02-22T15:06", "\u0421\u043e\u0444\u044c\u044f \u0411\u0430\u0440\u0430\u043d\u043d\u0438\u043a 15:06")] },
	{ id: "prp_mock2", key: "alias/barannik-s-sonya", status: "draft", kind: "Alias", op: "create", summary: "Alias \u0421\u043e\u043d\u044f", why: "Bogdan asks to write to \u0421\u043e\u043d\u044f in the chat Sofya is in", error: null, fields: [["value", "\u0421\u043e\u043d\u044f"], ["type", "SHORT"], ["of", "person/barannik-s"]], sources: [chatSource("src_mock2", 246, "2026-04-28T00:43", "\u0410 \u043d\u0430\u043f\u0438\u0448\u0435\u0448\u044c \u0421\u043e\u043d\u0435?")] },
	{ id: "prp_mock3", key: "payment/virality-final", status: "approved", kind: "StatusFact", op: "create", summary: "StatusFact PAID", why: null, error: null, fields: [["of", "payment/virality-final"], ["value", "PAID"], ["date", "2026-10-27"]], sources: [{ id: "src_mock3", type: "STATEMENT", title: "statement-20261027.txt", path: "statement/statement-20261027.txt", lines: { from: 2, to: 2 }, page: null, date: "2026-10-27T09:40", quote: "The 700k for Virality came in today", file: null }] },
] as (Omit<ReviewItem, "questions" | "answers"> & Partial<ReviewItem>)[]).map((p) => ({ questions: [], answers: {}, ...p }));

/** the mock home's managed instances (fleet.update edits them in place) */
const fleet: Managed[] = [
	{ ...fleetBase, id: "dpl_mock1", instance: "digiterium", url: "https://hq-digiterium.example.dev", domain: null, build: "99a83efdb78d", running: "06838318f695", ready: true, available: null, usage: [meter("requests", 61_200, 100_000, "day")] },
	{ ...fleetBase, id: "dpl_mock2", instance: "michael", url: "https://hq.example.dev", domain: "hq.example.dev", build: "99a83efdb78d", running: "99a83efdb78d", ready: true, available: "58aa448fc14a", usage: [meter("requests", 12_400, 100_000, "day")] },
	{ ...fleetBase, id: "dpl_mock3", instance: "whyti", url: "https://hq.whyti.co", domain: "hq.whyti.co", build: "99a83efdb78d", running: "99a83efdb78d", ready: true, available: "58aa448fc14a", held: "waiting for michael", usage: [meter("requests", 3_100, 100_000, "day")] },
];

export function mockKit(wasm: string): Promise<Kit> {
	kit ??= createPlatformKit({ origin: "https://michael.mock.invalid", token: MOCK_TOKEN, person: ME, replica: true, live: false, fetch: instance.fetch as typeof fetch, wasm });
	return kit;
}

export function createMockApi(latency: number, session: () => Promise<Kit>): Api {
	const core = createKitApi({ kit: session, once: session, person: () => ME, net: {} as Net, refresh: async () => null });
	const me = () => state.persons.find((x) => x.id === ME)!;
	const ago = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
	const signIns: SignIn[] = [
		{ id: "ses_1", kind: "app", agent: "Mozilla/5.0 (X11; Linux x86_64; rv:156.0) Gecko/20100101 Firefox/156.0", createdAt: ago(30), usedAt: ago(0), current: true },
		{ id: "ses_2", kind: "app", agent: "Mozilla/5.0 (iPhone; CPU iPhone OS 26_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/26.0 Mobile/15E148 Safari/604.1", createdAt: ago(80), usedAt: ago(3), current: false },
		{ id: "ses_3", kind: "device", agent: "hq-cli/0.4", createdAt: ago(200), usedAt: ago(26), current: false },
	];
	const apps: AppGrant[] = [{ id: "grt_1", name: "Claude", scopes: ["graph:read"], createdAt: ago(120) }];
	let seq = 1000;
	const nextId = (prefix: string, instance: string | null) => `${instance ? `${instance}/` : ""}${prefix}_${(seq++).toString(36)}`;
	return {
		session: {
			async me(token) {
				await sleep(latency);
				if (token !== MOCK_TOKEN) throw new ApiError("unauthorized", 401);
				return me();
			},
			async get() {
				await sleep(latency);
				return { person: me(), instance: "michael", tokenName: "mock", permissions: ["*", "usage.read"], projects: [] };
			},
		},
		snapshot: core.snapshot,
		board: core.board,
		graph: core.graph,
		entities: core.entities,
		chat: core.chat,
		tasks: {
			context: core.tasks.context,
			// whole words in the title or the body, like the instance's full-text search
			async search(q) {
				await sleep(latency);
				const words = q.toLowerCase().split(/\s+/).filter(Boolean);
				const text = (title: string) => ` ${`${title} ${pocBody(title)}`.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ")} `;
				return state.tasks.filter((t) => words.every((w) => text(t.title).includes(` ${w} `))).map((t) => t.id);
			},
		},
		instances: {
			list: async () => structuredClone(INSTANCES),
		},
		ontologies: {
			async list() {
				await sleep(latency);
				return structuredClone(p.packages);
			},
			async preview(name, version, given) {
				await sleep(latency);
				if (given) {
					const kinds = (given.kinds as { name: string }[] | undefined) ?? [];
					return { name, from: null, to: version, added: kinds.map((k) => ({ what: "kind" as const, name: `${name}.${k.name}`, detail: "addKind" })), refused: [], hooks: [] };
				}
				const pkg = p.packages.find((x) => x.name === name);
				if (!pkg) throw new ApiError("not found", 404);
				return diffOf(pkg, version);
			},
			async install(name, version, consent, given) {
				await sleep(latency * 3);
				if (given) throw new ApiError("Install from JSON needs a live instance (sample data)", 400);
				const pkg = p.packages.find((x) => x.name === name);
				if (!pkg) throw new ApiError("not found", 404);
				if (pkg.hooks.length && !consent) throw new ApiError("the package has hooks: the owner's consent is needed", 403);
				pkg.installed = version;
				pkg.installedAt = new Date().toISOString();
				for (const k of pkg.kinds) k.count = 0;
			},
		},
		access: {
			async sessions() {
				await sleep(latency);
				return structuredClone(signIns);
			},
			async endSession(id) {
				await sleep(latency);
				signIns.splice(signIns.findIndex((s) => s.id === id) >>> 0, 1);
			},
			async endOthers() {
				await sleep(latency);
				signIns.splice(0, signIns.length, ...signIns.filter((s) => s.current));
			},
			async apps() {
				await sleep(latency);
				return structuredClone(apps);
			},
			async revokeApp(id) {
				await sleep(latency);
				apps.splice(apps.findIndex((a) => a.id === id) >>> 0, 1);
			},
		},
		connections: {
			async list() {
				await sleep(latency);
				return structuredClone(p.connections);
			},
			async connect(url) {
				await sleep(latency);
				const name = url.match(/hq-([a-z0-9-]+)\./)?.[1] ?? url.replace(/^https?:\/\//, "").split(/[./]/)[0]!;
				p.connections.push({ id: nextId("cnx", null), direction: "outgoing", instance: name, url, person: me().name, scopes: ["graph:read", "graph:write"], createdAt: new Date().toISOString(), lastUsedAt: null, status: "pending" });
				return { redirect: null };
			},
			async revoke(id) {
				await sleep(latency);
				const c = p.connections.find((x) => x.id === id);
				if (c) c.status = "revoked";
			},
		},
		members: {
			async invite(space, roles) {
				await sleep(latency);
				return `https://hq.example.dev/auth/invite/mock-${space}-${roles.join("+")}-${(seq++).toString(36)}`;
			},
			async list() {
				await sleep(latency);
				return structuredClone(p.members);
			},
			roles: async () => structuredClone(p.roles),
			async add(person, roles, space) {
				await sleep(latency);
				const who = state.persons.find((x) => x.id === person.id || x.name === person.name) ?? { id: nextId("usr", null), name: person.name ?? "?", kind: "human" as const };
				const sp = p.spaces.find((x) => x.id === space);
				p.members.push({ id: nextId("mem", null), person: who, space: sp ? { id: sp.id, name: sp.name } : null, roles, status: "active" });
			},
			async remove(id) {
				await sleep(latency);
				const m = p.members.find((x) => x.id === id);
				if (m) m.status = "removed";
			},
		},
		tokens: {
			async list() {
				await sleep(latency);
				return structuredClone(p.tokens);
			},
			async create(name) {
				await sleep(latency);
				const token = { id: nextId("pat", null), name, createdAt: new Date().toISOString(), lastUsedAt: null, expiresAt: null, revoked: false };
				p.tokens.unshift(token);
				return { token: structuredClone(token), secret: `hqp_mock${Math.random().toString(36).slice(2, 14)}` };
			},
			async revoke(id) {
				await sleep(latency);
				const t = p.tokens.find((x) => x.id === id);
				if (t) t.revoked = true;
			},
		},
		...mockSecrets(latency),
		// mock builds sign in with the token "mock" directly (store/auth.ts); no auth service
		auth: {
			discover: async () => ({ instance: "michael", auth: null }),
			login: async (_a, pat) => ({ access: pat, refresh: "", expiresIn: 3600 }),
			refresh: async () => {
				throw new ApiError("no auth service", 401);
			},
			logout: async () => {},
			browser: async () => null,
		},
		cloudflare: {
			token: async () => {
				throw new ApiError("no Cloudflare in mock builds");
			},
		},
		// a batch of three proposals read from a chat (approve / reject edit them in place)
		knowledge: {
			async batch() {
				await sleep(latency);
				return review.map((p) => ({ ...p }));
			},
			async decide(ids, status) {
				await sleep(latency);
				for (const p of review) if (ids.includes(p.id)) p.status = status;
			},
			async answer(id, answers) {
				await sleep(latency);
				for (const p of review) if (p.id === id) p.answers = answers;
			},
			async sources(at) {
				await sleep(latency);
				return review.flatMap((p) => p.sources.filter((s) => s.id === at || s.file === at).map((s) => ({ ...s, records: [{ id: p.key, kind: p.kind, label: p.summary, status: p.status }] })));
			},
		},
		// a home that manages two instances: one rolling out a new build, one with a newer release to update to
		fleet: {
			async list() {
				await sleep(latency);
				return fleet.map((d) => ({ ...d, available: d.available === d.build ? null : d.available }));
			},
			async update(id, patch) {
				await sleep(latency);
				const d = fleet.find((x) => x.id === id);
				if (d) Object.assign(d, patch);
			},
		},
		// no files in the mock: an empty space, uploads refused
		files: {
			list: async () => ({ folders: [], files: [] }),
			versions: async () => [],
			upload: async () => {
				throw new ApiError("no files in mock builds");
			},
			folder: async () => {
				throw new ApiError("no files in mock builds");
			},
			open: async () => {
				throw new ApiError("no files in mock builds");
			},
		},
		blobs: {
			put: async () => {
				throw new ApiError("no blobs in mock builds");
			},
			get: async () => new Blob(["mock"], { type: "text/plain" }),
			direct: (url) => {
				const img = url.match(/^blob:\/\/mock\/(\w+\.jpg)$/)?.[1];
				return img ? `/mock/img/${img}` : null;
			},
			async open(url) {
				const img = url.match(/^blob:\/\/mock\/(\w+\.jpg)$/)?.[1];
				if (img) return URL.createObjectURL(await (await fetch(`/mock/img/${img}`)).blob());
				return URL.createObjectURL(new Blob(["mock"], { type: "text/plain" }));
			},
		},
	};
}
